import { useState } from 'react';
import { Bar } from '../../components/ui/Bar.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { parseCamt054, rapprocheCamt } from '../../lib/camt';
import { DEVISE, fmt } from '../../lib/currency';
import { tr, trf } from '../../lib/i18n';
import { today } from '../../lib/utils';
import { isoVersDate } from '../../api/mappers';
import { totauxDocument } from '../../lib/vat';
import { C } from '../../theme/palette';

/**
 * Paiements reçus : le relevé camt.054 de l'e-banking rapproche chaque
 * versement de sa facture par la référence QR, et la marque payée.
 *
 * <p>Il vivait dans la page des factures ; il rejoint la comptabilité, à côté
 * du relevé bancaire complet.
 *
 * @param {object} props propriétés
 * @param {object[]} props.factures factures de la société
 * @param {Function} props.setFactures mise à jour des factures
 * @param {object[]} props.clients clients, pour les noms
 * @param {Map<number, object[]>} [props.paiements] versements déjà inscrits, par facture
 * @returns {import('react').ReactElement} la carte d'import
 */
export function PaiementsFactures({ factures, setFactures, clients, paiements }) {
  const [camtRes, setCamtRes] = useState(null);
  const calc = (f) => totauxDocument(f.lignes, f.tvaPct, f.remisePct);
  return (
    <Card style={{ marginBottom: 11 }}>
      <Bar
        icon="🏦"
        title="Paiements reçus — relevé bancaire"
        color={C.green}
        sub="Importe le fichier camt.054 de ton e-banking"
      />
      <div style={{ padding: 13 }}>
        <label
          style={{
            display: 'block',
            background: C.greenL,
            border: '1px dashed ' + C.green,
            borderRadius: 10,
            padding: '13px',
            textAlign: 'center',
            cursor: 'pointer',
          }}
        >
          <input
            type="file"
            accept=".xml,text/xml,application/xml"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files && e.target.files[0];
              if (!file) return;
              const rd = new FileReader();
              rd.onload = () => {
                const ents = parseCamt054(String(rd.result || ''));
                const matches = rapprocheCamt(ents, factures, (f) => calc(f).ttc, paiements);
                // Le dernier versement de chaque facture donne son total et
                // son jour de paiement (celui de la banque, pas de l'import).
                const aNoter = new Map();
                matches.filter((m) => !m.deja).forEach((m) => aNoter.set(m.id, m));
                if (aNoter.size) {
                  setFactures((prev) =>
                    prev.map((f) => {
                      const m = aNoter.get(f.id);
                      return m
                        ? {
                            ...f,
                            statut: m.statut,
                            paidAmount: m.paidTotal,
                            paidDate: m.date ? isoVersDate(m.date) : today(),
                            vu: true,
                            vuLe: today(),
                          }
                        : f;
                    }),
                  );
                }
                setCamtRes({ total: ents.filter((x) => x.credit).length, matches });
              };
              rd.readAsText(file);
              e.target.value = '';
            }}
          />
          <div style={{ fontSize: 11, fontWeight: 800, color: C.green }}>
            📥 Importer un relevé camt.054
          </div>
          <div style={{ fontSize: 8.5, color: C.grey, marginTop: 3 }}>
            Chaque versement est rapproché de sa facture via la référence QR.
          </div>
          <div style={{ fontSize: 8.5, color: C.grey, marginTop: 4 }}>
            {tr(
              'Le fichier camt.054 (XML) se télécharge d’abord dans ton e-banking, puis se choisit ici.',
            )}
          </div>
        </label>
        {camtRes && (
          <div style={{ marginTop: 10 }}>
            {camtRes.matches.length === 0 ? (
              <div style={{ fontSize: 10, color: C.amber, fontWeight: 700 }}>
                Aucun paiement n'a pu être rapproché ({camtRes.total} crédit(s) lu(s)). Vérifie que
                les factures portent bien la référence QR.
              </div>
            ) : (
              <div>
                {camtRes.matches.map((m, i) => {
                  const cl = clients.find((c) => c.id === m.clientId);
                  return (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        background: m.statut === 'Payée' ? C.greenL : C.warm,
                        borderRadius: 9,
                        padding: '9px 11px',
                        marginBottom: 6,
                      }}
                    >
                      <span style={{ fontSize: 15 }}>{m.statut === 'Payée' ? '✅' : '➗'}</span>
                      <div style={{ flex: 1, fontSize: 10 }}>
                        <b>{cl?.nom || tr('Client')}</b>{' '}
                        {/* Le numéro de la facture, pas son identifiant interne
                            (« a payé 551 » pour la facture n° 03). */}
                        {trf(
                          m.acompte ? 'a versé l’acompte n° {0}' : 'a payé la facture n° {0}',
                          (factures || []).find((f) => f.id === m.id)?.num || m.id,
                        )}
                        <div style={{ fontSize: 8.5, color: C.grey }}>
                          {DEVISE} {fmt(m.montant)} {tr('reçu')} ·{' '}
                          {m.deja
                            ? tr('déjà enregistré, rien à refaire')
                            : m.statut === 'Payée'
                              ? tr('facture soldée')
                              : tr('paiement partiel')}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
